import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { Package, Save, Plus, Hash, Calendar, Layers } from 'lucide-react';
import { useAuth } from '../utils/AuthContext';
import { saveStationaryBookings } from '../utils/db/bookings';

const StationaryPurchase = () => {
  const { user } = useAuth();
  const [purchaseMode, setPurchaseMode] = useState('single'); // 'single' or 'multiple'
  const [isLoading, setIsLoading] = useState(false);

  const { register, handleSubmit, reset, formState: { errors } } = useForm();

  // Helper to generate an array of AWBs from a range
  const generateAwbRange = (fromStr, toStr) => {
    fromStr = fromStr.trim();
    toStr = toStr.trim();

    // Extract prefix, numeric part, and optional suffix
    // Matches: any characters (prefix), sequence of digits (number), any characters (suffix)
    const matchFrom = fromStr.match(/^(.*?)(\d+)(.*?)$/);
    const matchTo = toStr.match(/^(.*?)(\d+)(.*?)$/);

    if (!matchFrom || !matchTo) {
      throw new Error('AWB numbers must have a numeric part to increment.');
    }

    const prefix = matchFrom[1];
    const suffix = matchFrom[3];
    const numFrom = parseInt(matchFrom[2], 10);
    const numTo = parseInt(matchTo[2], 10);

    if (numFrom > numTo) {
      throw new Error('"From" docket number must be less than or equal to "To" docket.');
    }

    const maxCount = 500; // Limit to prevent accidental huge ranges
    if (numTo - numFrom + 1 > maxCount) {
      throw new Error(`Cannot generate more than ${maxCount} dockets at once.`);
    }

    const padLength = matchFrom[2].length;
    const awbs = [];
    for (let i = numFrom; i <= numTo; i++) {
      const numStr = i.toString().padStart(padLength, '0');
      awbs.push(`${prefix}${numStr}${suffix}`);
    }

    return awbs;
  };

  const onSubmit = async (data) => {
    setIsLoading(true);
    try {
      let awbsToSave = [];

      if (purchaseMode === 'single') {
        awbsToSave = [data.awbNo.trim()];
      } else {
        awbsToSave = generateAwbRange(data.docketFrom, data.docketTo);
      }

      if (awbsToSave.length === 0) {
        throw new Error('No dockets to save.');
      }

      const result = await saveStationaryBookings(user.id, awbsToSave, data.purchaseDate);

      if (result.failed > 0) {
        toast.warning(`Saved ${result.success} dockets, but ${result.failed} failed. See console for details.`);
        console.error('Failed dockets:', result.errors);
      } else {
        toast.success(`Successfully added ${result.success} docket(s)!`);
        
        // Reset form but keep the date
        reset({
          purchaseDate: data.purchaseDate,
          awbNo: '',
          docketFrom: '',
          docketTo: ''
        });
      }
    } catch (error) {
      console.error('Purchase creation error:', error);
      toast.error(`Failed to add dockets: ${error.message || 'Unknown error'}`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center">
          <Package className="h-6 w-6 mr-2 text-indigo-600" />
          Stationary Purchase
        </h1>
        <p className="text-gray-600 mt-1">Record new docket purchases into inventory</p>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="px-6 py-5 border-b border-gray-200 bg-gray-50/50">
          <div className="flex flex-col sm:flex-row sm:items-center space-y-3 sm:space-y-0 sm:space-x-6">
            <span className="text-sm font-semibold text-gray-700">Purchase Mode:</span>
            <label className="flex items-center space-x-2 cursor-pointer group">
              <input
                type="radio"
                className="w-4 h-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                checked={purchaseMode === 'single'}
                onChange={() => setPurchaseMode('single')}
              />
              <span className="text-sm text-gray-600 group-hover:text-gray-900 font-medium">Single Docket</span>
            </label>
            <label className="flex items-center space-x-2 cursor-pointer group">
              <input
                type="radio"
                className="w-4 h-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                checked={purchaseMode === 'multiple'}
                onChange={() => setPurchaseMode('multiple')}
              />
              <span className="text-sm text-gray-600 group-hover:text-gray-900 font-medium">Multiple Dockets</span>
            </label>
          </div>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            
            {/* Date Input - Common for both modes */}
            <div className="md:col-span-2 lg:col-span-1">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Purchase Date *
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Calendar className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="date"
                  {...register('purchaseDate', { required: 'Purchase date is required' })}
                  className="w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                  defaultValue={new Date().toISOString().split('T')[0]}
                />
              </div>
              {errors.purchaseDate && (
                <p className="mt-1 text-sm text-red-600">{errors.purchaseDate.message}</p>
              )}
            </div>

            {/* Conditional Inputs based on Mode */}
            {purchaseMode === 'single' ? (
              <div className="md:col-span-2 lg:col-span-1">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  AWB Number *
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Hash className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    {...register('awbNo', {
                      required: purchaseMode === 'single' ? 'AWB number is required' : false,
                      pattern: {
                        value: /^[A-Za-z0-9]+$/,
                        message: 'AWB must contain only letters and numbers'
                      }
                    })}
                    className="w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors font-mono font-bold text-indigo-700 uppercase"
                    placeholder="e.g. D330088"
                  />
                </div>
                {errors.awbNo && (
                  <p className="mt-1 text-sm text-red-600">{errors.awbNo.message}</p>
                )}
              </div>
            ) : (
              <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Docket From *
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Layers className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      {...register('docketFrom', {
                        required: purchaseMode === 'multiple' ? 'Starting docket is required' : false,
                        pattern: {
                          value: /^[A-Za-z0-9]+$/,
                          message: 'Must contain only letters and numbers'
                        }
                      })}
                      className="w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors font-mono font-bold text-indigo-700 uppercase"
                      placeholder="e.g. D330088"
                    />
                  </div>
                  {errors.docketFrom && (
                    <p className="mt-1 text-sm text-red-600">{errors.docketFrom.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Docket To *
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Layers className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      {...register('docketTo', {
                        required: purchaseMode === 'multiple' ? 'Ending docket is required' : false,
                        pattern: {
                          value: /^[A-Za-z0-9]+$/,
                          message: 'Must contain only letters and numbers'
                        }
                      })}
                      className="w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors font-mono font-bold text-indigo-700 uppercase"
                      placeholder="e.g. D330095"
                    />
                  </div>
                  {errors.docketTo && (
                    <p className="mt-1 text-sm text-red-600">{errors.docketTo.message}</p>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-end pt-4 border-t border-gray-100">
            <button
              type="submit"
              disabled={isLoading}
              className="flex items-center px-6 py-2.5 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
            >
              {isLoading ? (
                <div className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Processing...
                </div>
              ) : (
                <>
                  <Plus className="h-5 w-5 mr-2" />
                  Add to Inventory
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default StationaryPurchase;
